import _, {first} from 'lodash';
import {connect} from 'react-redux';

import {supportsPopout} from '../../../../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {onlyItem} from '../../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {MessageTypesEnum} from '../../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {postAsync} from '../../../../../../../libs/shared/core/src/services/http/http';
import {
  BulkActionsTrackingTypesEnum,
  InterfaceEventsEnum,
  PopoutLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ResolveStateProps} from '../../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {makeGetWillShowTriageForTeamAlert} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionSelectors';
import {
  ConversationStatusesEnum,
  makeFindConversationActionContext,
} from '../../../../../../helpers/models/conversationHelpers';
import {createConversationBaseLightStabilizer} from '../../../../../../helpers/models/conversationStabilityHelpers';
import {ActivityVisualTypesEnum} from '../../../../../../models/api/activityModel';
import {ConversationBaseModel, ConversationModel} from '../../../../../../models/api/conversationModel';
import {AppState} from '../../../../../../store/appState';
import {ThunkAction} from '../../../../../../store/appThunk';
import {getCompanyHost} from '../../../../../../store/data/company/companySelectors';
import {makeGetVisualRecipient} from '../../../../../../store/data/contacts/recipientsSelectors';
import {
  makeGetAreAllInternal,
  makeGetAreAllShared,
  makeGetAreAllSpam,
  makeGetAreAllSubscribed,
  makeGetReminderForAll,
} from '../../../../../../store/data/conversations/conversationsMultiSelectors';
import {
  makeGetConversationContextNamespaces,
  OptionalConversationBaseSelector,
} from '../../../../../../store/data/conversations/conversationsSelectors';
import {
  toggleBulkConversationAsSeenBulkAction,
  toggleMarkConversationAsSeenAction,
} from '../../../../../../store/data/conversations/seenConversationsActions';
import {setRecipientIsBlockedAsyncAction} from '../../../../../../store/data/conversations/setRecipientIsBlockedAction';
import {
  setBulkConversationsStatusAction,
  setConversationsStatusAction,
} from '../../../../../../store/data/conversations/statusConversationsActions';
import {
  subscribeToConversationsAction,
  unsubscribeFromConversationsAction,
} from '../../../../../../store/data/conversations/subscribeConversationsActions';
import {
  addAndRemoveTagsFromConversationsAction,
  bulkAddAndRemoveTagsFromConversationsAction,
} from '../../../../../../store/data/conversations/tagConversationsActions';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {makeGetIsPanelPopup} from '../../../../../../store/panels/panelsSelectors';
import {
  makeGetAreAllStarred,
  makeGetStarredTagId,
} from '../../../../../../store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {
  collapseAllMessagesAction,
  expandAllMessagesAsyncAction,
  popoutConversationViewerAction,
} from '../../../../../../store/panels/viewer/viewerPanelActions';
import {mergeConversationsAndNavigateAsyncAction} from '../../../../../../store/panels/viewer/viewerPanelConversationsActions';
import {
  makeGetTopbarPage,
  ViewerPanelPageSelector,
} from '../../../../../../store/panels/viewer/viewerPanelSelectors';
import {
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
} from '../../../../../../store/panels/viewer/viewerPanelState';
import {navigateToLinkAction} from '../../../../../../store/stateless/navigationActions';
import {ConversationsMoreCoordinator} from './conversationsMoreCoordinator';

/*
 * Props.
 */

interface ConversationsMoreCoordinatorContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  /** Potentially partial set of currently selected conversation. */
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
}

/*
 * Selectors.
 */

/** Extract the cells context from the props. */
function getCellsContext(_state: AppState, props: ConversationsMoreCoordinatorContainerProps) {
  return props.cellsContext;
}

/** Extract the panel id from the props. */
function getPanelId(_state: AppState, props: ConversationsMoreCoordinatorContainerProps) {
  return props.panelId;
}

function getOnlyConversation(_state: unknown, props: ConversationsMoreCoordinatorContainerProps) {
  return props.onlyConversation;
}

function getConversations(_state: unknown, props: ConversationsMoreCoordinatorContainerProps) {
  return props.conversations;
}

/** Extract the teammate ID from the cells context in the props. */
function getTeammateId(_state: AppState, props: ConversationsMoreCoordinatorContainerProps) {
  return props.cellsContext.teammateId;
}

function makeGetCanPopout() {
  return createSelector(
    [makeGetIsPanelPopup(getPanelId)],
    (isPanelPopup) => !isPanelPopup && supportsPopout(),
  );
}

/** Check if all the conversations are snoozed. */
function makeGetAreAllSnoozed() {
  const getReminderForAll = makeGetReminderForAll(getCellsContext, getConversations);
  return createSelector([getReminderForAll], (reminderForAll) => Boolean(reminderForAll));
}

function makeGetFirstConversation(): OptionalConversationBaseSelector<ConversationsMoreCoordinatorContainerProps> {
  const stabilizeConversation = createConversationBaseLightStabilizer();

  return (state, props) => stabilizeConversation(first(getConversations(state, props)));
}

function makeGetCanExpandAll() {
  return createSelector([getOnlyConversation], (onlyConversation) => {
    if (!onlyConversation) {
      return false;
    }

    return onlyConversation.messageType === MessageTypesEnum.EMAIL && onlyConversation.numMessages > 1;
  });
}

function makeGetIsAllExpanded(
  getCurrentPage: ViewerPanelPageSelector<ConversationsMoreCoordinatorContainerProps>,
) {
  return createSelector(
    [getCurrentPage],
    (page) =>
      (page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION && page.shouldExpandMessages) || false,
  );
}

function makeGetIsAllCollapsed(
  getCurrentPage: ViewerPanelPageSelector<ConversationsMoreCoordinatorContainerProps>,
) {
  return createSelector([getCurrentPage], (page) => {
    if (page.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return false;
    }

    return _(page.activities)
      .values()
      .compact()
      .map((a) => a.type === ActivityVisualTypesEnum.MESSAGE && a)
      .compact()
      .every((a) => !a.isExpanded);
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationsMoreCoordinatorContainerProps>()(() => {
  const getCurrentConversation = makeGetFirstConversation();
  const getCurrentPage = makeGetTopbarPage();

  return {
    host: getCompanyHost,
    canPopout: makeGetCanPopout(),
    currentPage: getCurrentPage,
    canExpandAll: makeGetCanExpandAll(),
    isAllExpanded: makeGetIsAllExpanded(getCurrentPage),
    isAllCollapsed: makeGetIsAllCollapsed(getCurrentPage),
    recipient: makeGetVisualRecipient(
      getCurrentConversation,
      getTeammateId,
      'conversationsMoreCoordinatorContainer',
    ),
    areAllShared: makeGetAreAllShared(getConversations),
    areAllSpam: makeGetAreAllSpam(getCellsContext, getConversations),
    areAllInternal: makeGetAreAllInternal(getConversations),
    areAllSubscribed: makeGetAreAllSubscribed(getCellsContext, getConversations),
    areAllSnoozed: makeGetAreAllSnoozed(),
    areAllStarred: makeGetAreAllStarred(getCellsContext, getConversations),
    starredTagId: makeGetStarredTagId(getCellsContext),
    contextNamespaces: wrapInShallowSelector(
      makeGetConversationContextNamespaces(getCellsContext, getConversations),
    ),
    shouldShowSpamTeamAlert: makeGetWillShowTriageForTeamAlert(
      getCellsContext,
      getConversations,
      TriageActionsEnum.SPAM,
    ),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ConversationsMoreCoordinatorContainerProps>()((props) => ({
  setRecipientIsBlockedAsync: setRecipientIsBlockedAsyncAction,
  setConversationsStatus: setConversationsStatusAction,
  setBulkConversationsStatus: setBulkConversationsStatusAction,
  toggleMarkConversationAsSeen: toggleMarkConversationAsSeenAction,
  toggleBulkConversationAsSeenBulk: toggleBulkConversationAsSeenBulkAction,
  addAndRemoveTagsFromConversations: addAndRemoveTagsFromConversationsAction,
  bulkAddAndRemoveTagsFromConversations: bulkAddAndRemoveTagsFromConversationsAction,
  mergeConversationsAndNavigateAsync: mergeConversationsAndNavigateAsyncAction,
  navigateToLink: navigateToLinkAction,
  onPopoutClick: (): ThunkAction => (dispatch, getState) => {
    const {inboxesParameters, conversations} = props;
    const conversation = onlyItem(conversations);
    if (!conversation) {
      return;
    }

    dispatch(
      popoutConversationViewerAction(
        conversation.id,
        undefined,
        undefined,
        inboxesParameters,
        PopoutLocationsEnum.MORE_MENU,
      ),
    );
  },
  subscribeToConversations: subscribeToConversationsAction,
  unsubscribeFromConversations: unsubscribeFromConversationsAction,
  expandAllMessagesAsync: expandAllMessagesAsyncAction,
  collapseAllMessages: collapseAllMessagesAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ConversationsMoreCoordinatorContainerProps,
) {
  const {starredTagId} = stateProps;
  const {inboxesParameters, cellsContext, conversations, onInterfaceEvent, panelId} = ownProps;
  const conversationIds = conversations.map((c) => c.id);

  const onExpandClick = () => {
    dispatchProps.expandAllMessagesAsync(panelId);
  };
  const onCollapseClick = () => {
    dispatchProps.collapseAllMessages(panelId);
  };

  const onBlockRecipientClick = () => {
    const conversation = onlyItem(conversations);
    if (!conversation) {
      return;
    }

    dispatchProps.setRecipientIsBlockedAsync(conversation.id, true);
    onMarkAsSpam();
  };

  const onUnblockRecipientClick = () => {
    const conversation = onlyItem(conversations);
    if (!conversation) {
      return;
    }

    dispatchProps.setRecipientIsBlockedAsync(conversation.id, false);
    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.OPEN,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onMarkAsSpam = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_SPAM, false);
    }

    dispatchProps.setConversationsStatus(
      cellsContext,
      conversationIds,
      ConversationStatusesEnum.SPAM,
      makeFindConversationActionContext(cellsContext, conversations),
    );
  };

  const onBulkMarkAsSpam = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_SPAM, true);
    }

    dispatchProps.setBulkConversationsStatus(inboxesParameters, cellsContext, ConversationStatusesEnum.SPAM);
  };

  const onReportPhishingClick = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.REPORT_PHISHING, false);
    }

    const {onlyConversation} = ownProps;
    if (!onlyConversation) {
      return;
    }

    postAsync(`${onlyConversation.url}/report/phishing`);
  };

  const onMarkAsRead = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_READ, false);
    }

    dispatchProps.toggleMarkConversationAsSeen(conversationIds, true, {cellsContext});
  };

  const onMarkAsUnread = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_UNREAD, false);
    }

    const seenAt = findMarkAsUnreadSeenAt(stateProps.currentPage);
    dispatchProps.toggleMarkConversationAsSeen(conversationIds, false, {seenAt, cellsContext});
  };

  const onBulkMarkAsRead = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_READ, true);
    }

    dispatchProps.toggleBulkConversationAsSeenBulk(inboxesParameters, cellsContext, true);
  };

  const onBulkMarkAsUnread = () => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MARK_UNREAD, true);
    }

    const seenAt = findMarkAsUnreadSeenAt(stateProps.currentPage);
    dispatchProps.toggleBulkConversationAsSeenBulk(inboxesParameters, cellsContext, false, seenAt);
  };

  const onStar = () => {
    if (!starredTagId) {
      return;
    }

    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.STAR, false);
    }

    dispatchProps.addAndRemoveTagsFromConversations(
      conversationIds,
      {
        add: [starredTagId],
      },
      true,
    );
  };

  const onUnstar = () => {
    if (!starredTagId) {
      return;
    }

    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.UNSTAR, false);
    }

    dispatchProps.addAndRemoveTagsFromConversations(
      conversationIds,
      {
        remove: [starredTagId],
      },
      true,
    );
  };

  const onBulkStar = () => {
    if (!starredTagId) {
      return;
    }

    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.STAR, true);
    }

    dispatchProps.bulkAddAndRemoveTagsFromConversations(inboxesParameters, cellsContext, {
      add: [starredTagId],
    });
  };

  const onBulkUnstar = () => {
    if (!starredTagId) {
      return;
    }

    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.UNSTAR, true);
    }

    dispatchProps.bulkAddAndRemoveTagsFromConversations(inboxesParameters, cellsContext, {
      remove: [starredTagId],
    });
  };

  const onMergeClick = async () => {
    trackInterfaceEvent(InterfaceEventsEnum.MERGE_DUPLICATE_CONVERSATIONS_MANUALLY);

    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.MERGE, false);
    }

    await dispatchProps.mergeConversationsAndNavigateAsync(conversationIds);
  };

  const onSubscribeClick = () => {
    dispatchProps.subscribeToConversations(cellsContext, conversationIds);
  };

  const onUnsubscribeClick = () => {
    dispatchProps.unsubscribeFromConversations(cellsContext, conversationIds);
  };

  return {
    ...stateProps,
    ...ownProps,
    onBlockRecipientClick,
    onUnblockRecipientClick,
    onMarkAsSpam,
    onBulkMarkAsSpam,
    onReportPhishingClick,
    onMarkAsRead,
    onMarkAsUnread,
    onBulkMarkAsRead,
    onBulkMarkAsUnread,
    onStar,
    onUnstar,
    onBulkStar,
    onBulkUnstar,
    onMergeClick,
    onPopoutClick: dispatchProps.onPopoutClick,
    onSubscribeClick,
    onUnsubscribeClick,
    onExpandClick,
    onCollapseClick,
  };
}

export const ConversationsMoreCoordinatorContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ConversationsMoreCoordinator);

/*
 * Helpers.
 */

function findMarkAsUnreadSeenAt(page: ViewerPanelPageState): number | undefined {
  const originalConversationState =
    page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ? page.originalConversationState : undefined;

  // If there have been updates, use our custom seenAt, otherwise reset the whole conversation
  return originalConversationState &&
    (!originalConversationState.seenAt ||
      originalConversationState.seenAt < originalConversationState.lastActivityDate)
    ? originalConversationState.seenAt
    : 0;
}
